import { graphql } from 'app/generated/gql'
import { useQuery } from 'urql'
import { useUser } from 'app/utils'
import { isOrgRole } from 'app/types/roles'
import type { LiveAlertPreferences, ResolvedLiveAlertPreferences } from 'app/types/live-alerts'
import { resolveLiveAlertPreferences } from 'lib/liveAlertPreferences'

export const LIVE_ALERT_PREFERENCES_QUERY = graphql(`
  query LiveAlertPreferences($userId: ID!) {
    userNotificationSettings(userId: $userId) {
      textMessages {
        newTextMessage {
          popup
          sound
        }
      }
      phoneCalls {
        incomingCall {
          popup
          sound
        }
      }
    }
  }
`)

/**
 * Live alert preferences for the alert sources.
 *
 * `loaded` matters: on is the default, so until the setting arrives there is no
 * telling it apart from someone having switched it off, and a card that appears
 * for half a second reads as the switch not working. Alerts are transient, so
 * the sources wait rather than guess.
 */
export function useLiveAlertPreferences(): ResolvedLiveAlertPreferences & { loaded: boolean } {
  const me = useUser()
  // Org-level users (super, or role >= orgUser) receive no workspace live alerts. Force both
  // channels off and skip the query, so no card pops and no chime plays regardless of defaults.
  const orgLevel = !!me && (me.type === `super` || isOrgRole({ roleName: me.roleName }))
  const [{ data, fetching, error }] = useQuery({
    query: LIVE_ALERT_PREFERENCES_QUERY,
    variables: { userId: me?.id ?? '' },
    pause: !me?.id || orgLevel,
  })
  if (orgLevel) {
    const off = { popup: false, sound: false }
    return { sms: off, calls: off, loaded: true }
  }
  const settings = data?.userNotificationSettings
  const stored: LiveAlertPreferences | undefined = settings
    ? {
        newTextMessage: settings.textMessages?.newTextMessage
          ? {
              popup: settings.textMessages.newTextMessage.popup ?? undefined,
              sound: settings.textMessages.newTextMessage.sound ?? undefined,
            }
          : undefined,
        incomingCall: settings.phoneCalls?.incomingCall
          ? {
              popup: settings.phoneCalls.incomingCall.popup ?? undefined,
              sound: settings.phoneCalls.incomingCall.sound ?? undefined,
            }
          : undefined,
      }
    : undefined
  // `data`, not `settings`: the API answers null for org-level users, whose notifications it
  // never stores, and that answer means the defaults rather than a load still in flight.
  return { ...resolveLiveAlertPreferences(stored), loaded: !!me?.id && !fetching && (!!data || !!error) }
}
